import React from "react";
import {
  Box,
  Pagination,
  Stack,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputBase,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const CustomSelectInput = styled(InputBase)(({ theme }) => ({
  "& .MuiInputBase-input": {
    borderRadius: 8,
    position: "relative",
    backgroundColor: theme.palette.background.paper,
    border: "1px solid #ced4da",
    fontSize: 14,
    padding: "8px 26px 8px 12px",
    transition: theme.transitions.create(["border-color", "box-shadow"]),
    color: "#4CAF50",
    "&:focus": {
      borderRadius: 8,
      borderColor: "#4CAF50",
      boxShadow: "0 0 0 0.2rem rgba(76, 175, 80, 0.25)",
    },
  },
}));

function CustomPagination({
  count,
  page,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  rowsPerPageOptions = [5, 10, 25, 50, 100],
  startIndex,
  endIndex,
}) {
  const onPageChanges = (event, value) => {
    onPageChange(event, value);
  };

  const totalPages = Math.ceil(count / rowsPerPage);

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 2, sm: 4 }}
        alignItems="center"
        justifyContent="space-between"
        sx={{
          width: "100%",
          p: 2,
        }}
      >
        <Typography variant="body2" sx={{ color: "#666", whiteSpace: "nowrap" }}>
          Show {startIndex} to {endIndex} of {count} results
        </Typography>

        <Pagination
          count={totalPages}
          page={page}
          onChange={onPageChanges}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          shape="rounded"
          color="primary"
          renderItem={(item) => (
            <Box
              component="li"
              sx={{
                listStyle: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 32,
                height: 32,
                borderRadius: "8px",
                mx: 0.5,
                fontWeight: "bold",
                cursor: item.disabled ? "not-allowed" : "pointer", // Disable cursor if item is disabled
                transition: "background-color 0.2s, color 0.2s",
                ...(item.selected && {
                  backgroundColor: "#4CAF50",
                  color: "white",
                }),
                ...(!item.selected &&
                  item.type === "page" && {
                    color: "#4CAF50",
                    backgroundColor: "transparent",
                    "&:hover": {
                      backgroundColor: "rgba(76, 175, 80, 0.1)",
                    },
                  }),
                ...(item.type === "ellipsis" && {
                  color: "#666",
                  fontWeight: "normal",
                  cursor: "default",
                  backgroundColor: "transparent",
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }),
                // Styles for previous/next buttons
                ...(item.type === "previous" || item.type === "next"
                  ? {
                      backgroundColor: "transparent",
                      border: "1px solid #4CAF50",
                      color: "#4CAF50",
                      "& .MuiSvgIcon-root": {
                        color: "#4CAF50",
                      },
                      "&:hover": {
                        backgroundColor: "rgba(76, 175, 80, 0.1)",
                      },
                      // Disable styles
                      ...(item.disabled && {
                        borderColor: "#ccc",
                        color: "#ccc",
                        cursor: "not-allowed",
                        "& .MuiSvgIcon-root": {
                          color: "#ccc",
                        },
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                      }),
                    }
                  : {}),
              }}
              onClick={item.onClick}
              disabled={item.disabled}
            >
              {item.type === "previous" && "<"}
              {item.type === "next" && ">"}
              {item.type === "page" && item.page}
              {item.type === "ellipsis" && "..."}
            </Box>
          )}
          sx={{
            "& .MuiPaginationItem-icon": {
              color: "#4CAF50",
            },
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center", whiteSpace: "nowrap" }}>
          <Typography variant="body2" sx={{ color: "#666", mr: 1 }}>
            Per page
          </Typography>
          <FormControl variant="standard">
            <Select
              value={rowsPerPage}
              onChange={onRowsPerPageChange}
              input={<CustomSelectInput />}
              IconComponent={() => (
                <KeyboardArrowDownIcon
                  sx={{
                    color: "#4CAF50",
                    pointerEvents: "none",
                    position: "absolute",
                    right: 8,
                  }}
                />
              )}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: "8px",
                    boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
                    border: "1px solid #4CAF50",
                    "& .MuiMenuItem-root": {
                      color: "#4CAF50",
                      "&:hover": {
                        backgroundColor: "rgba(76, 175, 80, 0.1)",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "rgba(76, 175, 80, 0.2)",
                        color: "#4CAF50",
                      },
                    },
                  },
                },
              }}
            >
              {rowsPerPageOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Stack>
    </Box>
  );
}

export default CustomPagination;